import { useQueries, useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'

export type ExchangeRateQuote = { rate: string; rate_date: string }

/**
 * The rate the API suggests for a document of a currency and date
 * (foreign-currency suppliers, Decision 2): the last rate on or before the
 * date. A 422 means the company holds no such rate; the query then errors and
 * the form asks the user for one. The company currency is never quoted — its
 * rate is 1 by definition, so callers pass `null` for it.
 */
export function exchangeRateQuoteQuery(
  companyId: string | null,
  currency: string | null,
  date: string,
) {
  return {
    queryKey: ['companies', companyId, 'accounting', 'rate-quote', currency, date] as const,
    enabled: Boolean(companyId && currency && date),
    retry: false,
    staleTime: 60_000,
    queryFn: async (): Promise<ExchangeRateQuote> => {
      const response = await api.GET('/accounting/exchange-rates/quote', {
        headers: { 'X-Company-Id': companyId ?? '' },
        params: { query: { currency: currency ?? '', date } },
      })
      if (!response.data) throw new Error(response.error?.message ?? 'no_rate_for_date')
      return response.data.data
    },
  }
}

export function useExchangeRateQuote(
  companyId: string | null,
  currency: string | null,
  date: string,
) {
  return useQuery(exchangeRateQuoteQuery(companyId, currency, date))
}

/**
 * One quote per foreign currency at one date, as a map from currency to rate.
 * A currency still loading or without a rate is simply absent from the map.
 */
export function useExchangeRateQuotes(
  companyId: string | null,
  currencies: string[],
  date: string,
): Map<string, string> {
  const unique = [...new Set(currencies)].sort()
  const results = useQueries({
    queries: unique.map((currency) => exchangeRateQuoteQuery(companyId, currency, date)),
  })
  const rates = new Map<string, string>()
  unique.forEach((currency, index) => {
    const rate = results[index]?.data?.rate
    if (rate) rates.set(currency, rate)
  })
  return rates
}
